import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { FOCO_VISIVEL } from 'utils/foco';

import { CartaoDeExperiencia as Cartao, formatarData } from '../catalogo-modelo';
import { PurchaseOutcome } from '../purchaseOutcome';
import { DesfechoDaTroca } from '../trocaDeExperiencia';
import ModalDoCatalogo from './ModalDoCatalogo';
import './modal-de-resgate.scss';

/** A partir daqui o aviso de vagas vira o aviso de fila cheia (RES-07). */
export const AVISO_DE_FILA_MS = 45_000;

type Falha = Exclude<PurchaseOutcome, { kind: 'confirmada' }>;
type TrocaRecusada = Extract<DesfechoDaTroca, { kind: 'mantida' | 'sem_resgate' | 'cancelamento_falhou' }>;
type Etapa =
  | { nome: 'confirmacao' }
  | { nome: 'processando' }
  | { nome: 'resultado'; falha: Falha }
  | { nome: 'troca_recusada'; desfecho: TrocaRecusada }
  | { nome: 'sucesso' };

const NAO_EFETIVADO = 'O Resgate dessa experiência não foi efetivado.';

function textoDaFalha(falha: Falha) {
  switch (falha.kind) {
    case 'esgotada':
      return { titulo: NAO_EFETIVADO, comNome: true, texto: 'esgotou a quantidade de vagas. Selecione uma outra experiência disponível no Catálogo.', cor: 'text-[#D4224A]' };
    case 'recusada':
      return { titulo: NAO_EFETIVADO, comNome: false, texto: falha.message, cor: 'text-[#D4224A]' };
    case 'sem_confirmacao':
      return { titulo: 'Não recebemos a confirmação.', comNome: false, texto: 'Verifique no catálogo antes de tentar de novo.', cor: 'text-[#8A6D00]' };
  }
}

const BOTAO = `flex flex-[1_1_160px] cursor-pointer items-center justify-center rounded-md border-0 px-5 py-[13px] font-text text-[13px] font-bold leading-[normal] uppercase tracking-[0.04em] no-underline transition-colors ${FOCO_VISIVEL}`;
// SPEC_DEVIATION: texto `brand-blue-dark` nos dois botões (design `brand-blue`: 4,44:1 no amarelo, 3,94:1 no cinza).
const BOTAO_PRINCIPAL = `${BOTAO} bg-brand-yellow text-brand-blue-dark hover:bg-button-primary-hover hover:text-brand-blue-dark hover:no-underline`;
const BOTAO_SECUNDARIO = `${BOTAO} bg-surface-200 text-brand-blue-dark hover:bg-surface-300`;
const TITULO = 'm-0 mb-3 pr-8 font-display text-[24px] font-extrabold leading-[normal] text-brand-blue-dark outline-none';

function Aviso({ children, icone = 'info' }: { children: ReactNode; icone?: 'info' | 'fila' }) {
  return (
    <div className="flex gap-2.5 rounded-lg bg-brand-yellow-light px-3.5 py-3 text-left">
      <span aria-hidden="true" className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-blue">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--brand-white)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" focusable="false">
          {icone === 'info' ? (
            <>
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </>
          ) : (
            <>
              <circle cx="12" cy="8" r="4" />
              <path d="M5 21a7 7 0 0 1 14 0" />
            </>
          )}
        </svg>
      </span>
      <p className="m-0 text-[13px] leading-[1.5] text-content">{children}</p>
    </div>
  );
}

function IconeDaEtapa({ cor, children }: { cor: string; children: ReactNode }) {
  return (
    <div aria-hidden="true" className={`mb-[18px] flex h-14 w-14 items-center justify-center rounded-full ${cor}`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" focusable="false">
        {children}
      </svg>
    </div>
  );
}

interface ModalDeResgateProps {
  /** Cartão escolhido; sem ele o modal fica fechado. */
  cartao?: Cartao;
  /** Fim do prazo do ciclo: até quando a escolha pode ser trocada. */
  fim?: Date;
  regulamentoUrl: string;
  resgatar: (cartao: Cartao) => Promise<PurchaseOutcome>;
  onFechar: () => void;
  /** Vai para a página da experiência escolhida. */
  onVerExperiencia: () => void;
  /** Nome da experiência já resgatada no ciclo: com ele, o modal pede a troca (TRC-10). */
  escolhaAtual?: string;
  /** Cancela a escolha atual e resgata o cartão (TRC-12 a TRC-16). */
  trocar?: (cartao: Cartao) => Promise<DesfechoDaTroca>;
}

/** O desfecho da troca no vocabulário das etapas do modal. */
function etapaDaTroca(desfecho: DesfechoDaTroca): Etapa {
  switch (desfecho.kind) {
    case 'trocada':
      return { nome: 'sucesso' };
    case 'sem_confirmacao':
      return { nome: 'resultado', falha: { kind: 'sem_confirmacao' } };
    default:
      return { nome: 'troca_recusada', desfecho };
  }
}

/**
 * Modal de resgate (CAT-20 a CAT-23): confirmação com o aceite do regulamento, processamento,
 * desfecho sem resgate e, no sucesso, o "Escolha registrada!" do design. Com uma escolha atual, a
 * confirmação pede a troca e os desfechos dela ganham o painel "A troca não foi realizada." (TRC-10 a TRC-16).
 */
export default function ModalDeResgate({ cartao, fim, regulamentoUrl, resgatar, onFechar, onVerExperiencia, escolhaAtual, trocar }: ModalDeResgateProps) {
  const [etapa, setEtapa] = useState<Etapa>({ nome: 'confirmacao' });
  const [ciente, setCiente] = useState(false);
  const [filaCheia, setFilaCheia] = useState(false);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  const tituloId = useId();
  const descricaoId = useId();
  const cienteId = useId();
  const textoDoCienteId = useId();

  // Cada abertura começa da confirmação, com a caixa do regulamento desmarcada.
  useEffect(() => {
    setEtapa({ nome: 'confirmacao' });
    setCiente(false);
  }, [cartao?.id]);

  const processando = etapa.nome === 'processando';

  useEffect(() => {
    setFilaCheia(false);
    if (!processando) return;
    const temporizador = window.setTimeout(() => setFilaCheia(true), AVISO_DE_FILA_MS);
    return () => window.clearTimeout(temporizador);
  }, [processando]);

  // Ao abrir e a cada troca de etapa, o foco vai para o título da etapa visível (RES-04, RES-14).
  useEffect(() => {
    if (cartao) tituloRef.current?.focus();
  }, [etapa, cartao]);

  if (!cartao) return null;

  const emTroca = !!escolhaAtual && !!trocar;

  const confirmar = async () => {
    if (cartao.esgotada) return;
    setEtapa({ nome: 'processando' });
    if (emTroca && trocar) {
      setEtapa(etapaDaTroca(await trocar(cartao)));
      return;
    }
    const desfecho = await resgatar(cartao);
    setEtapa(desfecho.kind === 'confirmada' ? { nome: 'sucesso' } : { nome: 'resultado', falha: desfecho });
  };

  // Na troca, fechar devolve à vitrine, que relê a escolha gravada (TRC-17); no resgate, o sucesso leva à experiência.
  const fechar = etapa.nome === 'sucesso' && !emTroca ? onVerExperiencia : processando ? undefined : onFechar;
  const prazo = fim ? formatarData(fim) : '';

  return (
    <ModalDoCatalogo
      isOpen
      tituloId={tituloId}
      descricaoId={descricaoId}
      onFechar={fechar}
      rotuloFechar={etapa.nome === 'confirmacao' ? 'Fechar modal de confirmação' : 'Fechar'}
    >
      {etapa.nome === 'confirmacao' && (
        <>
          <h2 id={tituloId} ref={tituloRef} tabIndex={-1} className={TITULO}>
            {emTroca ? 'Deseja trocar de experiência?' : 'Atenção!'}
          </h2>
          {emTroca ? (
            <p id={descricaoId} className="m-0 mb-5 text-[15px] leading-[1.6]">
              Para resgatar <strong>{cartao.nomeDaEscolha}</strong>, a experiência já resgatada, <strong>{escolhaAtual}</strong>, será
              cancelada.
            </p>
          ) : (
            <p id={descricaoId} className="m-0 mb-5 text-[15px] leading-[1.6]">
              Você pode trocar sua escolha até <strong>{prazo}</strong>, na página da sua experiência.
              <strong> Após essa data, a escolha se torna definitiva.</strong>
            </p>
          )}
          <div className="mb-5 rounded-xl bg-surface-100 px-4 py-3.5">
            <h3 className="m-0 mb-1 font-display text-[14px] font-bold text-brand-blue-dark">Sua escolha:</h3>
            <p className="m-0 text-[15px] leading-[1.5] text-content">
              {cartao.escolha.quantidade && <>{cartao.escolha.quantidade} </>}
              <strong className="text-brand-blue-dark">{cartao.escolha.destaque}</strong>
              {cartao.escolha.complemento && <span className="block text-[13px] text-content-soft">{cartao.escolha.complemento}</span>}
            </p>
            {cartao.aviso && (
              <p role="note" className="m-0 mt-2 text-[13px] font-bold leading-[1.5] text-brand-blue-dark">
                {cartao.aviso}
              </p>
            )}
          </div>
          <fieldset className="m-0 mb-5 flex items-start gap-3 border-0 p-0">
            <legend className="sr-only">Confirmação de ciência do regulamento</legend>
            <input
              id={cienteId}
              type="checkbox"
              checked={ciente}
              onChange={evento => setCiente(evento.target.checked)}
              className={`mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-brand-blue ${FOCO_VISIVEL}`}
            />
            <label id={textoDoCienteId} htmlFor={cienteId} className="cursor-pointer text-[13px] leading-[1.5]">
              Declaro que estou ciente do{' '}
              <a href={regulamentoUrl} target="_blank" rel="noopener noreferrer" className={`font-bold text-brand-blue-dark underline ${FOCO_VISIVEL}`}>
                Regulamento<span className="sr-only"> (abre em nova aba)</span>
              </a>{' '}
              e das diretrizes do programa
            </label>
          </fieldset>
          <div className="mb-6">
            <Aviso>
              As vagas são limitadas. Confirmar envia sua solicitação para processamento e <strong>não garante o resgate</strong>.
            </Aviso>
          </div>
          <div role="group" aria-label="Ações de confirmação" className="flex flex-wrap gap-3">
            <button type="button" onClick={onFechar} className={BOTAO_SECUNDARIO}>
              {emTroca ? 'Não, manter a atual' : 'Voltar'}
            </button>
            <button
              type="button"
              onClick={confirmar}
              disabled={!ciente || cartao.esgotada}
              aria-describedby={!ciente ? textoDoCienteId : undefined}
              className={`${BOTAO_PRINCIPAL} disabled:cursor-not-allowed disabled:bg-surface-200 disabled:text-content-soft disabled:hover:bg-surface-200 disabled:hover:text-content-soft`}
            >
              {cartao.esgotada ? 'Experiência esgotada' : emTroca ? 'Sim, trocar' : 'Quero viver essa experiência'}
            </button>
          </div>
        </>
      )}

      {processando && (
        <>
          <h2 id={tituloId} ref={tituloRef} tabIndex={-1} className={TITULO}>
            Solicitação em processamento
          </h2>
          <p className="m-0 mb-4 font-bold text-brand-blue-dark">{cartao.nomeDaEscolha}</p>
          <div id={descricaoId} role="status" className="flex flex-col gap-4">
            <p className="m-0 flex items-center gap-3 font-bold text-brand-blue-dark">
              <span aria-hidden="true" className="h-6 w-6 shrink-0 rounded-full border-2 border-solid border-surface-200 border-t-brand-blue motion-safe:animate-spin" />
              Estamos processando sua solicitação
            </p>
            <div aria-hidden="true" className="relative h-1.5 w-full overflow-hidden rounded-full bg-surface-200">
              <div className="resgate-processando absolute top-0 h-full w-2/5 rounded-full bg-brand-blue" />
            </div>
            {filaCheia ? (
              <Aviso icone="fila">
                <strong>Muita gente escolhendo agora.</strong> Sua solicitação está na fila e pode levar mais alguns minutos. Pode
                continuar aqui: avisamos assim que terminar.
              </Aviso>
            ) : (
              <Aviso>
                As vagas são limitadas. <strong>Só ao final do processamento saberemos se você conseguiu o resgate.</strong>
              </Aviso>
            )}
          </div>
        </>
      )}

      {etapa.nome === 'resultado' && (
        <Resultado
          falha={etapa.falha}
          nome={cartao.nomeDaEscolha}
          tituloId={tituloId}
          descricaoId={descricaoId}
          tituloRef={tituloRef}
          // Na troca, repetir cancelaria de novo sem a vitrine reler o que ficou: o caminho é "Ver catálogo" (TRC-16).
          onTentarDeNovo={
            emTroca
              ? undefined
              : () => {
                  setCiente(false);
                  setEtapa({ nome: 'confirmacao' });
                }
          }
          onVerCatalogo={onFechar}
        />
      )}

      {etapa.nome === 'troca_recusada' && (
        <TrocaNaoRealizada
          desfecho={etapa.desfecho}
          nova={cartao.nomeDaEscolha}
          atual={escolhaAtual ?? ''}
          prazo={prazo}
          tituloId={tituloId}
          descricaoId={descricaoId}
          tituloRef={tituloRef}
          onVerCatalogo={onFechar}
        />
      )}

      {etapa.nome === 'sucesso' && (
        <>
          <IconeDaEtapa cor="bg-brand-yellow text-brand-blue">
            <path d="M20 6 9 17l-5-5" />
          </IconeDaEtapa>
          <h2 id={tituloId} ref={tituloRef} tabIndex={-1} className={TITULO}>
            Escolha registrada!
          </h2>
          <p id={descricaoId} className="m-0 mb-2 text-[15px] leading-[1.6]">
            Sua experiência escolhida é <b className="uppercase text-brand-blue">{cartao.nomeDaEscolha}</b>.
          </p>
          <p className="m-0 mb-[22px] text-[13px] leading-[1.6] text-brand-blue">
            Você pode alterar sua escolha até {prazo}. Encerrado o prazo, a experiência será processada automaticamente e você
            acompanha tudo no seu extrato.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/catalogo-de-experiencias" className={`${BOTAO_PRINCIPAL} flex-[1_1_180px]`}>
              Ver meu extrato
            </Link>
            <button type="button" onClick={onVerExperiencia} className={`${BOTAO_SECUNDARIO} flex-[1_1_140px]`}>
              Ver minha experiência
            </button>
          </div>
        </>
      )}
    </ModalDoCatalogo>
  );
}

interface ResultadoProps {
  falha: Falha;
  nome: string;
  tituloId: string;
  descricaoId: string;
  tituloRef: React.RefObject<HTMLHeadingElement>;
  /** Ausente, o painel sem confirmação fica só com "Ver catálogo". */
  onTentarDeNovo?: () => void;
  onVerCatalogo: () => void;
}

function Resultado({ falha, nome, tituloId, descricaoId, tituloRef, onTentarDeNovo, onVerCatalogo }: ResultadoProps) {
  const { titulo, comNome, texto, cor } = textoDaFalha(falha);
  return (
    <>
      <IconeDaEtapa cor={`bg-surface-100 ${cor}`}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5" />
        <path d="M12 16h.01" />
      </IconeDaEtapa>
      <h2 id={tituloId} ref={tituloRef} tabIndex={-1} className={TITULO}>
        {titulo}
      </h2>
      <p id={descricaoId} className="m-0 mb-[22px] text-[15px] leading-[1.6]">
        {comNome && (
          <>
            <strong>{nome}</strong>{' '}
          </>
        )}
        {texto}
      </p>
      <div className="flex flex-wrap gap-3">
        {falha.kind === 'sem_confirmacao' && onTentarDeNovo && (
          <button type="button" onClick={onTentarDeNovo} className={BOTAO_SECUNDARIO}>
            Tentar novamente
          </button>
        )}
        <button type="button" onClick={onVerCatalogo} className={BOTAO_PRINCIPAL}>
          Ver catálogo
        </button>
      </div>
    </>
  );
}

interface TrocaNaoRealizadaProps {
  desfecho: TrocaRecusada;
  /** Nome da experiência pedida. */
  nova: string;
  /** Nome da experiência resgatada antes da troca. */
  atual: string;
  prazo: string;
  tituloId: string;
  descricaoId: string;
  tituloRef: React.RefObject<HTMLHeadingElement>;
  onVerCatalogo: () => void;
}

// As mensagens de recusa já terminam em ponto; o painel põe o seu depois do motivo.
const semPontoFinal = (texto: string) => texto.replace(/\.$/, '');

/** O texto do painel para cada recusa da troca (TRC-13 a TRC-15). */
function TextoDaTrocaNaoRealizada({ desfecho, nova, atual, prazo }: Pick<TrocaNaoRealizadaProps, 'desfecho' | 'nova' | 'atual' | 'prazo'>) {
  switch (desfecho.kind) {
    case 'mantida':
      return (
        <>
          <strong>{nova}</strong> não pôde ser resgatada: {semPontoFinal(desfecho.motivo)}. Sua experiência continua sendo <strong>{atual}</strong>.
        </>
      );
    case 'sem_resgate':
      return (
        <>
          <strong>{nova}</strong> não pôde ser resgatada: {semPontoFinal(desfecho.motivo)}. Você está sem experiência resgatada e pode escolher outra até{' '}
          {prazo}.
        </>
      );
    case 'cancelamento_falhou':
      return (
        <>
          {desfecho.mensagem} Sua experiência continua sendo <strong>{atual}</strong>.
        </>
      );
  }
}

function TrocaNaoRealizada({ tituloId, descricaoId, tituloRef, onVerCatalogo, ...texto }: TrocaNaoRealizadaProps) {
  return (
    <>
      <IconeDaEtapa cor="bg-surface-100 text-[#D4224A]">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5" />
        <path d="M12 16h.01" />
      </IconeDaEtapa>
      <h2 id={tituloId} ref={tituloRef} tabIndex={-1} className={TITULO}>
        A troca não foi realizada.
      </h2>
      <p id={descricaoId} className="m-0 mb-[22px] text-[15px] leading-[1.6]">
        <TextoDaTrocaNaoRealizada {...texto} />
      </p>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onVerCatalogo} className={BOTAO_PRINCIPAL}>
          Ver catálogo
        </button>
      </div>
    </>
  );
}
